import React, { useState } from 'react';
import Text from '../Text';
import { navigate } from 'gatsby';
import { cn } from '@/lib/utils';
import { GTM } from '@src/analytics/gtm';
import { gtmEvents } from '@src/analytics/gtmEventsConstant';

const roles = [
  {
    label: 'Entrepreneurs',
    icon: '/images/GenAI/IconOne.png',
    slug: 'entrepreneurs',
    features: [
      {
        title: 'Process Automation',
        desc: 'Automate data entry, streamline approvals, optimize workflows \u2014 significant reduction in manual tasks with fewer errors.',
        image: '/images/GenAI/Domains/entrepreneurs-feature-1.webp',
      },
      {
        title: 'Data Analysis',
        desc: 'Monitor KPIs, predict bottlenecks, optimize resource allocation \u2014 faster insights with proactive problem-solving.',
        image: '/images/GenAI/Domains/entrepreneurs-feature-2.webp',
      },
      {
        title: 'Resource Planning',
        desc: 'Plan staffing needs, allocate resources, optimize schedules \u2014 better resource utilization with reduced overtime.',
        image: '/images/GenAI/Domains/entrepreneurs-feature-3.webp',
      },
    ],
  },
  {
    label: 'Product & Product Marketing Management',
    icon: '/images/GenAI/IconTwo.png',
    slug: 'product-marketing-management',
    features: [
      {
        title: 'User & Market Research',
        desc: 'Understand customer needs, track competitors, prioritize problems \u2014 faster product-market fit.',
        image: '/images/GenAI/Domains/product-feature-1.webp',
      },
      {
        title: 'Product Ideation & Validation',
        desc: 'Validate ideas quickly, test usability, prioritize roadmap \u2014 reduced risk of building the wrong thing.',
        image: '/images/GenAI/Domains/product-feature-2.webp',
      },
      {
        title: 'Product Roadmapping',
        desc: 'Plan sprints, align teams, adapt quickly \u2014 improved team agility.',
        image: '/images/GenAI/Domains/product-feature-3.webp',
      },
    ],
  },
  {
    label: 'Marketing',
    icon: '/images/GenAI/IconThree.png',
    slug: 'marketing',
    features: [
      {
        title: 'Content Creation',
        desc: 'Produce blog posts, ads, emails, social content \u2014 faster production, improved engagement.',
        image: '/images/GenAI/Domains/marketing-feature-1.webp',
      },
      {
        title: 'Audience Intelligence',
        desc: 'Target high-value users, personalize messaging, reduce churn \u2014 better ROAS.',
        image: '/images/GenAI/Domains/marketing-feature-2.webp',
      },
      {
        title: 'Campaign Optimization',
        desc: 'Increase conversions, lower CAC, maximize spend efficiency \u2014 improved ROI.',
        image: '/images/GenAI/Domains/marketing-feature-3.webp',
      },
    ],
  },
  {
    label: 'Consulting',
    icon: '/images/GenAI/IconFour.png',
    slug: 'consulting',
    features: [
      {
        title: 'Research & Market Intelligence',
        desc: 'Produce faster client deliverables, identify market shifts \u2014 improve strategic recommendations.',
        image: '/images/GenAI/Domains/consulting-feature-1.webp',
      },
      {
        title: 'Strategic Framework Development',
        desc: 'Build custom strategies, test outcomes \u2014 enhance quality of client insights.',
        image: '/images/GenAI/Domains/consulting-feature-2.webp',
      },
      {
        title: 'Client Intelligence & Personalization',
        desc: 'Anticipate client needs, surface relevant opportunities \u2014 increase client satisfaction and retention.',
        image: '/images/GenAI/Domains/consulting-feature-3.webp',
      },
    ],
  },
  {
    label: 'Design/UI/UX',
    icon: '/images/GenAI/IconFive.png',
    slug: 'design-ui-ux',
    features: [
      {
        title: 'User Research & Insight Extraction',
        desc: 'Identify user needs faster, reduce manual research \u2014 improve design decisions.',
        image: '/images/GenAI/Domains/design-feature-1.webp',
      },
      {
        title: 'Design Ideation & Concept Generation',
        desc: 'Accelerate early-stage design, unlock new visual directions \u2014 increase creative output.',
        image: '/images/GenAI/Domains/design-feature-2.webp',
      },
      {
        title: 'Rapid Prototyping & Variants',
        desc: 'Create, test, and iterate designs faster \u2014 enhance design speed and experimentation.',
        image: '/images/GenAI/Domains/design-feature-3.webp',
      },
    ],
  },
  {
    label: 'Operations',
    icon: '/images/GenAI/IconSix.png',
    slug: 'operations',
    features: [
      {
        title: 'Process Automation',
        desc: 'Automate data entry, streamline approvals, optimize workflows \u2014 significant reduction in manual tasks with fewer errors.',
        image: '/images/GenAI/Domains/design-feature-3.webp',
      },
      {
        title: 'Data Analysis',
        desc: 'Monitor KPIs, predict bottlenecks, optimize resource allocation \u2014 faster insights with proactive problem-solving.',
        image: '/images/GenAI/Domains/operations-feature-2.webp',
      },
      {
        title: 'Resource Planning',
        desc: 'Plan staffing needs, allocate resources, optimize schedules \u2014 better resource utilization with reduced overtime.',
        image: '/images/GenAI/Domains/operations-feature-3.webp',
      },
    ],
  },
];

const AudienceIcons = () => {
  const [selected, setSelected] = useState(roles[0]); // Default selection
  const [openIdx, setOpenIdx] = useState(0);
  const currentFeature = selected.features[openIdx];
  console.log('🚀 ~ AudienceIcons ~ currentFeature:', currentFeature);

  const handleClick = (slug) => {
    navigate(`/gen-ai-certificate-program?slug=${slug}`);
  };

  return (
    <div className="py-8 px-4">
      {/* Icon Row */}
      <div className="mb-8 overflow-x-auto overflow-y-hidden">
        <div className="mx-auto flex w-max flex-nowrap gap-8 p-2 md:flex-wrap md:justify-center md:gap-10">
          {roles.map((role, i) => (
            <div
              key={i}
              className={`flex shrink-0 cursor-pointer flex-col items-center transition ${
                selected.slug === role.slug ? 'scale-105' : 'opacity-70'
              }`}
              onClick={() => {
                setSelected(role);
                GTM.track(gtmEvents.USER_CLICK_EVENT, {
                  type: 'GenAI Track Research Main Page Button Clicks',
                  domain: role.slug,
                });
                setOpenIdx(0);
              }}
            >
              <div
                className={cn(
                  'flex h-[70px] w-[70px] items-center justify-center rounded-full  p-4 shadow-md md:h-[100px] md:w-[100px]',
                  `${
                    selected.slug === role.slug
                      ? `bg-[#40F9CA]`
                      : 'bg-[#E9F7F6]'
                  }`,
                )}
              >
                <img
                  src={role.icon}
                  alt="icon"
                  className="h-[30px] w-[30px] md:h-[50px] md:w-[50px]"
                />
              </div>
              <Text className="mt-3 max-w-[140px] whitespace-normal text-center text-[12px] md:max-w-[200px] md:text-center md:text-[16px]">
                {role.label}
              </Text>
            </div>
          ))}
        </div>
      </div>

      {/* Card */}
      <div className="mx-auto flex max-w-[1200px] flex-col-reverse items-center justify-center rounded-2xl bg-[#E9F8F6] p-4 md:flex-row md:gap-6 md:p-6">
        {/* Accordion */}
        <div className="w-full max-w-[600px] flex-1">
          {selected.features.map((item, idx) => (
            <div
              key={idx}
              className="cursor-pointer border-b border-[#CBD5D1] py-3 md:py-8"
              onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
            >
              <div className="flex items-center justify-between">
                <Text className="text-[10px] text-black">{item.title}</Text>
                <span className="text-gray-500">
                  {openIdx === idx ? '▲' : '▼'}
                </span>
              </div>
              {openIdx === idx && item.desc && (
                <Text className="mt-3 text-left text-[10px]">{item.desc}</Text>
              )}
            </div>
          ))}

          <div className="mt-8 flex justify-center">
            <button
              onClick={() => handleClick(selected.slug)}
              className="rounded-md bg-[#FFD600] px-6 py-3 text-sm font-bold text-black hover:bg-[#facc15] md:text-base"
            >
              Explore Domain &rsaquo;
            </button>
          </div>
        </div>

        {/* Image */}
        <div className="w-full max-w-[600px] flex-1">
          <div className="w-full rounded-xl bg-gray-200 md:min-h-[500px]">
            <img
              src={
                currentFeature?.image ||
                '/images/GenAI/Domains/entrepreneurs-feature-1.webp'
              }
              alt={currentFeature?.title || 'Domain visual'}
              className="h-full w-full rounded-xl object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudienceIcons;
